import type { ApplicationIconKey, ApplicationsListData } from "@lifter/hub-ui/browser-shared";
import { type ApplicationActionName, hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

export function useApplicationsQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.applications(),
    queryFn: () => api.applications.list(),
    enabled: options.enabled ?? true,
  });
}

export function useInternalApplicationQuery(appId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.internalApplication(appId),
    queryFn: () => api.applications.getInternal(appId),
    enabled: (options.enabled ?? true) && appId.length > 0,
  });
}

export function useUpdateInternalApplicationVisibilityMutation(appId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (visibility: "agent" | "private") =>
      api.applications.updateVisibility(appId, visibility),
    onSuccess: (data) => {
      queryClient.setQueryData<ApplicationsListData>(hubQueryKeys.applications(), (current) =>
        current
          ? {
              ...current,
              internal: current.internal.map((application) =>
                application.appId === appId
                  ? { ...application, visibility: data.visibility }
                  : application,
              ),
            }
          : current,
      );
      void queryClient.invalidateQueries({ queryKey: hubQueryKeys.internalApplication(appId) });
      void queryClient.invalidateQueries({ queryKey: hubQueryKeys.applications() });
    },
  });
}

export function useRunApplicationActionMutation(appId: string) {
  const api = useHubApi();
  return useMutation({
    mutationFn: (input: { actionName: ApplicationActionName; args: unknown }) =>
      api.applications.runAction(appId, input.actionName, input.args),
  });
}

export function useCreateExternalApplicationMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; url: string; icon?: ApplicationIconKey }) =>
      api.applications.createExternal(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: hubQueryKeys.applications() }),
  });
}

export function useUpdateExternalApplicationMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      patch: { title?: string; url?: string; icon?: ApplicationIconKey | null };
    }) => api.applications.updateExternal(input.id, input.patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: hubQueryKeys.applications() }),
  });
}

export function useDeleteExternalApplicationMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.applications.deleteExternal(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: hubQueryKeys.applications() }),
  });
}

export function useDeleteInternalApplicationMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (appId: string) => api.applications.deleteInternal(appId),
    onSuccess: (_data, appId) => {
      queryClient.setQueryData<ApplicationsListData>(hubQueryKeys.applications(), (current) =>
        current
          ? {
              ...current,
              internal: current.internal.filter((application) => application.appId !== appId),
            }
          : current,
      );
      void queryClient.invalidateQueries({ queryKey: hubQueryKeys.applications() });
    },
  });
}
